import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import queryString from 'query-string'
import log from 'loglevel'
import { useAsyncInterval } from 'src/Utils/useAsyncInterval'
import { ChatServicePromiseClient } from '@trustero/trustero-api-web/lib/chat/chat_grpc_web_pb'
import {
  ChatMessage,
  GetIntermediateStatusResponse,
  StopChatRequest,
} from '@trustero/trustero-api-web/lib/chat/chat_pb'
import {
  Artifact,
  ArtifactType,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { ArtifactServicePromiseClient } from '@trustero/trustero-api-web/lib/artifact/service_grpc_web_pb'
import {
  ArtifactResponse,
  GetArtifactByIdRequest,
} from '@trustero/trustero-api-web/lib/artifact/service_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import {
  ClientMessageType,
  ConfirmationDataType,
  Progress,
} from '@trustero/trustero-api-web/lib/tichatapi/agent_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useArtifactPanelContext } from '../chatpanel/chatPanel.context'
import { useGetRecentMessages } from '../trusteroIntelligence.hooks'
import { TPR_RUN_CHECKS_MESSAGES } from '../tprConstants'

export type OnSend = (prompt: string, messageType?: ClientMessageType) => void

export const useArtifact = (
  artifactId: string,
): GrpcResponse<ArtifactResponse> => {
  const request = new GetArtifactByIdRequest().setId(artifactId)
  const { response } = useSwrImmutableGrpc(
    ArtifactServicePromiseClient.prototype.getArtifactById,
    request,
    !!artifactId,
  )
  return NewGrpcResponse(response)
}

export const useArtifactDeepLink = (
  conversation: ChatMessage[],
  containerRef: React.RefObject<HTMLDivElement>,
  onSend: OnSend,
): void => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const lastHandledArtifactIdRef = useRef<string | null>(null)
  const {
    artifactPanelOpen,
    selectedArtifactId,
    setArtifactPanelOpen,
    setSelectedArtifactId,
    setSelectedArtifactMessageId,
    setSelectedArtifactPreview,
    setSelectedArtifactMessagePreview,
    setOnSend,
  } = useArtifactPanelContext()

  const clearArtifactDeepLink = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('artifact')
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  // Parse URL parameters for deep linking
  useEffect(() => {
    const queryParams = queryString.parse(location.search)
    const artifactId = queryParams.artifact as string

    if (!artifactId) {
      lastHandledArtifactIdRef.current = null
      return
    }

    if (lastHandledArtifactIdRef.current === artifactId) {
      return
    }

    if (artifactId && !selectedArtifactId) {
      const messageWithArtifact = conversation.find((msg) =>
        msg
          .getAgentMessageMetadata()
          ?.getArtifactsList()
          ?.some((art) => art.getId() === artifactId),
      )
      if (!messageWithArtifact) {
        return
      }

      const artifact = messageWithArtifact
        .getAgentMessageMetadata()
        ?.getArtifactsList()
        ?.find((art) => art.getId() === artifactId)

      if (artifact) {
        lastHandledArtifactIdRef.current = artifactId
        setSelectedArtifactId(artifact.getId())
        setSelectedArtifactMessageId(messageWithArtifact.getId())
        setSelectedArtifactPreview(artifact)
        setSelectedArtifactMessagePreview(messageWithArtifact)
        setArtifactPanelOpen(true)
        setOnSend(
          () =>
            function (
              prompt: string,
              messageType: ClientMessageType | undefined,
            ) {
              clearArtifactDeepLink()
              setArtifactPanelOpen(false)
              setSelectedArtifactId(null)
              setSelectedArtifactMessageId(null)
              setSelectedArtifactPreview(null)
              setSelectedArtifactMessagePreview(null)
              setOnSend(null)
              onSend(prompt, messageType)
            },
        )

        setTimeout(() => {
          const artifactElement = document.querySelector(
            `[data-artifact-id="${artifactId}"]`,
          )
          if (artifactElement && containerRef.current) {
            artifactElement.scrollIntoView({
              behavior: 'auto',
              block: 'center',
            })
          }
        }, 100)
      }
    }
  }, [
    location.search,
    conversation,
    selectedArtifactId,
    setSelectedArtifactId,
    setSelectedArtifactMessageId,
    setSelectedArtifactPreview,
    setSelectedArtifactMessagePreview,
    setArtifactPanelOpen,
    containerRef,
    onSend,
    setOnSend,
    clearArtifactDeepLink,
  ])

  // Clear URL when artifact panel is closed
  useEffect(() => {
    if (!artifactPanelOpen && selectedArtifactId) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('artifact')
      setSearchParams(newParams, { replace: true })
    }
  }, [artifactPanelOpen, selectedArtifactId, searchParams, setSearchParams])
}

export const useMessageDeepLink = (
  conversation: ChatMessage[],
  containerRef: RefObject<HTMLDivElement>,
): {
  highlightedMessageId: string | undefined
} => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const scrollAttemptedRef = useRef(false)
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | undefined
  >(undefined)
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Parse URL parameters for deep linking to messages
  useEffect(() => {
    const queryParams = queryString.parse(location.search)
    const messageId = queryParams.messageId as string

    if (messageId && conversation.length > 0) {
      const message = conversation.find((msg) => msg.getId() === messageId)
      if (!message) {
        // Message not found yet, retry after a delay
        if (!scrollAttemptedRef.current) {
          scrollAttemptedRef.current = true
          const retryTimeout = setTimeout(() => {
            scrollAttemptedRef.current = false
          }, 2000)
          return () => clearTimeout(retryTimeout)
        }
        return
      }

      scrollAttemptedRef.current = true

      // Set highlighted message and scroll to it after delays to ensure DOM is ready
      const scrollTimeout = setTimeout(() => {
        const messageElement = document.querySelector(
          `[data-message-id="${messageId}"]`,
        )
        if (messageElement) {
          // Clear any existing highlight timeout
          if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current)
          }

          // Set highlight
          setHighlightedMessageId(messageId)

          // Scroll to the message instantly so highlight is visible
          messageElement.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          })

          // Remove highlight after 3 seconds
          highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedMessageId(undefined)
            highlightTimeoutRef.current = null
          }, 3000)
        }
      }, 500)

      return () => {
        clearTimeout(scrollTimeout)
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current)
          highlightTimeoutRef.current = null
        }
        setHighlightedMessageId(undefined)
      }
    } else if (!messageId) {
      scrollAttemptedRef.current = false
      setHighlightedMessageId(undefined)
    }
  }, [location.search, conversation, containerRef])

  // Clear URL when scrolling is complete
  useEffect(() => {
    const queryParams = queryString.parse(location.search)
    const messageId = queryParams.messageId as string

    if (messageId && scrollAttemptedRef.current) {
      // Clear the messageId from URL after scrolling (with a delay to allow smooth scroll to complete)
      const timeoutId = setTimeout(() => {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('messageId')
        setSearchParams(newParams, { replace: true })
        scrollAttemptedRef.current = false
      }, 1500)

      return () => clearTimeout(timeoutId)
    }
  }, [location.search, searchParams, setSearchParams, conversation])

  return { highlightedMessageId }
}

export const useChatMessageConfirmation = (
  message: ChatMessage,
  nextUserMessage: ChatMessage | null | undefined,
): {
  confirmationDataType?: ConfirmationDataType
  yesText: string
  noText: string
  userResponded: boolean
  userRespondedYes: boolean
  userRespondedNo: boolean
  userRespondedAction: boolean
  hasTPRArtifact: boolean
} =>
  useMemo(() => {
    const confirmationDataType = message
      .getAgentMessageMetadata()
      ?.getConfirmationDataType()
    const textOptions = message.getAgentMessageMetadata()?.getConfirmationText()
    const yesText = textOptions?.getYes() ?? 'Yes'
    const noText = textOptions?.getNo() ?? 'No'
    const clientMessageType = nextUserMessage
      ?.getUserMessageMetadata()
      ?.getClientMessageType()
    const userResponded = !!nextUserMessage
    const userRespondedYes =
      clientMessageType === ClientMessageType.CLIENT_CONFIRMATION_YES
    const userRespondedNo =
      clientMessageType === ClientMessageType.CLIENT_CONFIRMATION_NO
    const userRespondedAction =
      userResponded &&
      clientMessageType === ClientMessageType.CLIENT_UNSPECIFIED &&
      TPR_RUN_CHECKS_MESSAGES.has(nextUserMessage?.getMessage() || '')
    const hasTPRArtifact =
      message
        .getAgentMessageMetadata()
        ?.getArtifactsList()
        ?.some(
          (a) =>
            a.getType() === ArtifactType.ARTIFACT_TEST_PROCEDURE_REFINEMENT,
        ) ?? false
    return {
      confirmationDataType,
      yesText,
      noText,
      userResponded,
      userRespondedYes,
      userRespondedNo,
      userRespondedAction,
      hasTPRArtifact,
    }
  }, [message, nextUserMessage])

const STREAMING_MESSAGES_INTERVAL = 2000

export const useStreamMessages = (
  chatId: string,
  latestMessageTimestamp: number,
): {
  messages: ChatMessage[]
  intermediateStatus: GetIntermediateStatusResponse | undefined
} => {
  const [intervalMs, setIntervalMs] = useState(STREAMING_MESSAGES_INTERVAL)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [intermediateStatus, setIntermediateStatus] = useState<
    GetIntermediateStatusResponse | undefined
  >(undefined)
  const getRecentMessages = useGetRecentMessages(chatId)

  const currentChatIdRef = useRef(chatId)

  useEffect(() => {
    setIntervalMs(STREAMING_MESSAGES_INTERVAL)
    currentChatIdRef.current = chatId
    // Clear state when switching chats
    setMessages([])
    setIntermediateStatus(undefined)
  }, [chatId])

  useAsyncInterval(async () => {
    const fetchMessages = async () => {
      if (!chatId) {
        return
      }

      const lastChatId = chatId
      const lastMessageTimestamp = latestMessageTimestamp
      const messagesMap = new Map<string, ChatMessage>()

      const updateMessages = (newMessages: ChatMessage[]) => {
        // Check that the latest message timestamp hasn't changed since the last fetch, otherwise
        // there was another poll triggered before this request completed.
        // Also check that there are new messages to add or old messages to clear.
        if (
          lastMessageTimestamp === latestMessageTimestamp &&
          (newMessages.length || messages.length)
        ) {
          newMessages.forEach((message) => {
            messagesMap.set(message.getId(), message)
          })
        }
      }

      try {
        const response = await getRecentMessages(latestMessageTimestamp)
        if (lastChatId !== currentChatIdRef.current) {
          return
        }
        updateMessages(response.messages)
        setIntermediateStatus(response.intermediateStatus)
      } catch (error) {
        if (
          error instanceof Error &&
          'message' in error &&
          error.message.includes('record not found')
        ) {
          log.info('Chat record not found, skipping fetch')
          setIntervalMs(0)
        } else {
          log.error('Error fetching recent messages', error)
        }
      }

      setMessages(
        Array.from(messagesMap.values()).sort(
          (a, b) => a.getUpdatedAt() - b.getUpdatedAt(),
        ),
      )
    }
    await fetchMessages()
  }, intervalMs)

  return {
    messages,
    intermediateStatus,
  }
}

export const useChatIntermediateStatus = (
  intermediateStatus: GetIntermediateStatusResponse | undefined,
): {
  busy: boolean | undefined
  percent: Progress | undefined
  message: string
  artifacts: Artifact[]
  cancelledTime: number | undefined
  progressCancellable: boolean | undefined
} => {
  const { agentBusy, setAgentBusy } = useArtifactPanelContext()

  const {
    message,
    busy,
    percent,
    artifacts,
    cancelledTime,
    progressCancellable,
  } = useMemo(() => {
    const message = stripMarkdownFences(intermediateStatus?.getMessage() || '')

    if (
      intermediateStatus?.getProgressCase() ===
      GetIntermediateStatusResponse.ProgressCase.BUSY
    ) {
      return {
        message,
        busy: true,
        percent: undefined,
        artifacts: intermediateStatus.getArtifactsList(),
        cancelledTime: intermediateStatus.getCancelledTime(),
        progressCancellable: intermediateStatus.getProgressCancellable(),
      }
    } else if (
      intermediateStatus?.getProgressCase() ===
      GetIntermediateStatusResponse.ProgressCase.PERCENT
    ) {
      return {
        message,
        busy: undefined,
        percent: intermediateStatus.getPercent(),
        artifacts: intermediateStatus.getArtifactsList(),
        cancelledTime: intermediateStatus.getCancelledTime(),
        progressCancellable: intermediateStatus.getProgressCancellable(),
      }
    } else {
      return {
        message,
        busy: undefined,
        percent: undefined,
        artifacts: [],
        cancelledTime: undefined,
        progressCancellable: undefined,
      }
    }
  }, [intermediateStatus])

  const hasBusyOrPercent = useMemo(() => {
    return busy || !!percent
  }, [busy, percent])

  useEffect(() => {
    if (hasBusyOrPercent && !agentBusy) {
      setAgentBusy(true)
    } else if (!hasBusyOrPercent && agentBusy) {
      setAgentBusy(false)
    }
  }, [hasBusyOrPercent, agentBusy, setAgentBusy])

  return {
    busy,
    percent,
    message,
    artifacts,
    cancelledTime,
    progressCancellable,
  }
}

export const useCancelChatProgress = (
  chatId: string,
): (() => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)
  return useCallback(async () => {
    const request = new StopChatRequest().setChatId(chatId)
    await client.stopChat(request)
  }, [chatId, client])
}

export const stripMarkdownFences = (markdown: string): string => {
  return markdown.replace(/```markdown\s*([\s\S]*?)\s*```/gi, '$1').trim()
}
